import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import BrandHeader from "@/components/BrandHeader";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";

const META = {
  terminos: {
    title: "Términos y condiciones · Talent Health Check · OptimizaRRHH",
    description: "Términos y condiciones del servicio Talent Health Check de OptimizaRRHH: alcance del diagnóstico, consultoría premium, pagos, reembolsos, propiedad intelectual y legislación aplicable.",
  },
  privacidad: {
    title: "Política de Privacidad · Talent Health Check · OptimizaRRHH",
    description: "Política de privacidad de Talent Health Check: datos recopilados, finalidad, base legal RGPD, terceros (Stripe, Resend, Anthropic) y derechos del interesado.",
  },
  cookies: {
    title: "Política de Cookies · Talent Health Check · OptimizaRRHH",
    description: "Política de cookies de Talent Health Check: cookies técnicas imprescindibles, duración, cookies de Stripe durante el checkout y gestión desde el navegador.",
  },
};

const CONTENT = {
  terminos: {
    title: "Términos y condiciones",
    kicker: "Marco legal",
    body: (
      <>
        <p>Bienvenido a Talent Health Check, servicio ofrecido por OptimizaRRHH. Al acceder o usar este servicio aceptas quedar vinculado por estos términos.</p>
        <h2>1. Servicios</h2>
        <p>El diagnóstico rápido (47&nbsp;€) es una evaluación estructurada del estado de tus procesos de gestión del talento. La consultoría premium (397&nbsp;€) añade informe personalizado, checklist accionable y un set operativo de 7 piezas generadas a medida por nuestro motor asistido.</p>
        <h2>2. Pagos y facturación</h2>
        <p>Todos los pagos se procesan a través de Stripe. La factura se emite automáticamente y se puede solicitar copia por email a hola@talenthealthcheck.com.</p>
        <h2>3. Reembolsos</h2>
        <p>Ofrecemos garantía de devolución de 14 días si el servicio no cumple con las expectativas. Contáctanos para gestionar el reembolso.</p>
        <h2>4. Propiedad intelectual</h2>
        <p>Los informes generados son de tu propiedad. La plataforma, el método y las herramientas son propiedad exclusiva de OptimizaRRHH.</p>
        <h2>5. Responsabilidad</h2>
        <p>Los informes son una guía profesional. Su aplicación es responsabilidad del cliente.</p>
        <h2>6. Legislación aplicable</h2>
        <p>Estos términos se rigen por la legislación española. Tribunales competentes: Barcelona.</p>
      </>
    ),
  },
  privacidad: {
    title: "Política de privacidad",
    kicker: "Datos personales",
    body: (
      <>
        <p>En OptimizaRRHH nos tomamos muy en serio la protección de tus datos. Esta política describe qué información recopilamos, cómo la usamos y tus derechos.</p>
        <h2>Responsable del tratamiento</h2>
        <p>OptimizaRRHH · hola@talenthealthcheck.com</p>
        <h2>Datos que recopilamos</h2>
        <ul>
          <li>Email y contraseña para acceso a tu cuenta.</li>
          <li>Nombre de empresa, país y número de empleados (contexto del análisis).</li>
          <li>Respuestas al cuestionario (base del análisis).</li>
          <li>Datos de pago procesados por Stripe (no almacenamos tarjeta).</li>
        </ul>
        <h2>Finalidad</h2>
        <p>Generar tu diagnóstico y consultoría, gestionar la cuenta y comunicaciones asociadas al servicio.</p>
        <h2>Base legal</h2>
        <p>Ejecución de contrato (art. 6.1.b RGPD) y consentimiento explícito (art. 6.1.a).</p>
        <h2>Terceros</h2>
        <p>Stripe (pagos), Resend (email), Anthropic Claude (generación de informes). Todos con acuerdos de tratamiento firmados.</p>
        <h2>Derechos</h2>
        <p>Puedes acceder, rectificar, suprimir, portar o limitar tus datos escribiendo a hola@talenthealthcheck.com. También puedes reclamar ante la AEPD.</p>
      </>
    ),
  },
  cookies: {
    title: "Política de cookies",
    kicker: "Navegación técnica",
    body: (
      <>
        <p>Este sitio usa cookies técnicas imprescindibles para el funcionamiento de la aplicación. No usamos cookies de terceros con fines publicitarios ni de tracking.</p>
        <h2>Cookies que usamos</h2>
        <ul>
          <li><strong>Sesión</strong>: mantener tu sesión iniciada tras el login. Duración: 30 días.</li>
          <li><strong>Preferencias UI</strong>: recordar estados como notas y prioridades en tu área privada. Duración: 1 año.</li>
        </ul>
        <h2>Cookies de terceros</h2>
        <p>Stripe (durante el checkout) inserta cookies imprescindibles para detectar fraude. Se usan solo en la pasarela de pago.</p>
        <h2>Gestión</h2>
        <p>Puedes borrar cookies desde la configuración de tu navegador. Ten en cuenta que si borras las cookies de sesión tendrás que volver a iniciar sesión.</p>
      </>
    ),
  },
};

export default function LegalPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const c = CONTENT[slug];
  if (!c) {
    navigate("/404", { replace: true });
    return null;
  }
  return (
    <>
      <Seo
        title={(META[slug] && META[slug].title) || c.title + " · OptimizaRRHH"}
        description={(META[slug] && META[slug].description) || c.title}
        canonical={`https://talenthealthcheck.com/legal/${slug}`}
      />
      <BrandHeader />
      <div
        className="min-h-screen py-8 md:py-12 px-4 md:px-6 overflow-x-hidden"
        style={{
          background:
            "radial-gradient(1200px 500px at 100% -10%, rgba(15,110,86,0.06) 0%, rgba(15,110,86,0) 60%)," +
            "radial-gradient(900px 400px at -10% 20%, rgba(201,168,76,0.06) 0%, rgba(201,168,76,0) 55%)," +
            "linear-gradient(180deg, #fafaf7 0%, #f5f5f1 100%)",
        }}
        data-testid={`legal-page-${slug}`}
      >
        <div className="max-w-3xl mx-auto">
          <button onClick={() => navigate(-1)} className="text-sm font-semibold text-slate-500 hover:text-[#0F6E56] inline-flex items-center gap-1.5 mb-6 group">
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" /> Atrás
          </button>
          <div className="rounded-3xl p-6 md:p-10" style={{ background: "linear-gradient(180deg, #ffffff 0%, #fdfcf6 100%)", boxShadow: "0 22px 45px -30px rgba(11,61,46,0.28), 0 0 0 1px rgba(11,61,46,0.06)" }}>
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-bold mb-4" style={{ color: "#0F6E56" }}>
              <span style={{ width: 24, height: 1.5, background: "#C9A84C", display: "inline-block" }} />
              {c.kicker}
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900 mb-6" style={{ letterSpacing: "-0.02em" }}>{c.title}</h1>
            <div className="prose prose-slate max-w-none consulting-md text-[15px]">{c.body}</div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}
